/**
 * Admin visual system — central color + label tokens.
 *
 * Use these helpers everywhere admin components render:
 *   - open-call status
 *   - curator rating
 *   - ticket decision
 *   - action buttons
 *   - section headers
 *
 * API values stay snake_case. Only display labels are humanized.
 */

// ─── Palette tokens ────────────────────────────────────────────
// Keep subtle on dark; meant to feel like elegant gallery labels.
const TONE = {
  neutral: {
    text: '#d6d6d6',
    border: '#2a2a2a',
    bg: 'rgba(255,255,255,0.025)',
    glow: 'rgba(255,255,255,0.10)',
  },
  muted: {
    text: '#9a9a9a',
    border: '#333333',
    bg: 'transparent',
    glow: 'rgba(255,255,255,0.06)',
  },
  cyan: {
    text: '#90E0EF',
    border: 'rgba(144,224,239,0.45)',
    bg: 'rgba(144,224,239,0.08)',
    glow: 'rgba(144,224,239,0.35)',
  },
  softBlue: {
    text: '#7aa8d4',
    border: 'rgba(122,168,212,0.4)',
    bg: 'rgba(122,168,212,0.07)',
    glow: 'rgba(122,168,212,0.28)',
  },
  blue: {
    text: '#6ab6d4',
    border: 'rgba(106,182,212,0.4)',
    bg: 'rgba(106,182,212,0.08)',
    glow: 'rgba(106,182,212,0.3)',
  },
  violet: {
    text: '#c89dff',
    border: 'rgba(200,157,255,0.42)',
    bg: 'rgba(200,157,255,0.08)',
    glow: 'rgba(200,157,255,0.35)',
  },
  green: {
    text: '#7ed4a0',
    border: 'rgba(126,212,160,0.4)',
    bg: 'rgba(126,212,160,0.08)',
    glow: 'rgba(126,212,160,0.3)',
  },
  amber: {
    text: '#e4c07a',
    border: 'rgba(228,192,122,0.45)',
    bg: 'rgba(228,192,122,0.08)',
    glow: 'rgba(228,192,122,0.3)',
  },
  red: {
    text: '#e88ba0',
    border: 'rgba(232,139,160,0.4)',
    bg: 'rgba(232,139,160,0.07)',
    glow: 'rgba(232,139,160,0.25)',
  },
  paleBlue: {
    text: '#a8d5e0',
    border: 'rgba(168,213,224,0.4)',
    bg: 'rgba(168,213,224,0.07)',
    glow: 'rgba(168,213,224,0.25)',
  },
  gold: {
    text: '#f0d48a',
    border: 'rgba(240,212,138,0.5)',
    bg: 'rgba(240,212,138,0.09)',
    glow: 'rgba(240,212,138,0.45)',
  },
};

// ─── Mappings ──────────────────────────────────────────────────

const OPEN_CALL_STATUS = {
  new: TONE.neutral,
  under_review: TONE.blue,
  shortlisted: TONE.violet,
  accepted: TONE.green,
  rejected: TONE.red,
  waitlist: TONE.amber,
  contacted: TONE.paleBlue,
};

const TICKET_STATUS = {
  pending: TONE.muted,
  accepted: TONE.green,
  declined: TONE.red,
  waitlist: TONE.amber,
};

const CURATOR_RATING = {
  average: TONE.muted,
  good: TONE.softBlue,
  very_good: TONE.cyan,
  genius: TONE.gold,
};

// ─── Label formatters ──────────────────────────────────────────

const LABEL_OVERRIDES = {
  very_good: 'Very Good',
};

export function humanize(value) {
  if (!value && value !== 0) return '—';
  if (LABEL_OVERRIDES[value]) return LABEL_OVERRIDES[value];
  return String(value)
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export const formatStatus = humanize;
export const formatRating = humanize;

// ─── Tone resolvers ────────────────────────────────────────────

export function toneForOpenCallStatus(s) {
  return OPEN_CALL_STATUS[s] || TONE.neutral;
}

export function toneForTicketStatus(s) {
  return TICKET_STATUS[s] || TICKET_STATUS.pending;
}

export function toneForRating(r) {
  return CURATOR_RATING[r] || TONE.muted;
}

// ─── Badge style (for <span> labels) ───────────────────────────

/**
 * Returns an inline-style object for a badge in the given tone.
 * @param {object} tone — one of the TONE tokens
 * @param {boolean} strong — add subtle glow / stronger bg for the "active" feel
 */
export function badgeStyle(tone, strong = false) {
  return {
    color: tone.text,
    borderColor: tone.border,
    background: strong ? tone.bg : 'transparent',
    boxShadow: strong ? `0 0 10px ${tone.glow}` : 'none',
  };
}

// ─── Button variants ───────────────────────────────────────────

// Button variants mapped to tones. 'primary' is the brand cyan.
const VARIANT_TONES = {
  primary: TONE.cyan,
  secondary: TONE.neutral,
  success: TONE.green,
  warning: TONE.amber,
  danger: TONE.red,
  purple: TONE.violet,
  blue: TONE.blue,
  gold: TONE.gold,
  ghost: TONE.muted,
  cyan: TONE.cyan,
};

export function toneForVariant(v) {
  return VARIANT_TONES[v] || TONE.neutral;
}

/**
 * Returns inline style + hover handlers for an action button.
 * Use in combination with base utility classes.
 * @param {string} variant — key of VARIANT_TONES
 * @param {object} opts — { active, disabled }
 */
export function actionButtonStyle(variant, opts = {}) {
  const tone = toneForVariant(variant);
  const { active = false, disabled = false } = opts;
  return {
    color: tone.text,
    borderColor: active ? tone.text : tone.border,
    background: active ? tone.bg : 'transparent',
    boxShadow: active ? `0 0 14px ${tone.glow}` : 'none',
    opacity: disabled ? 0.45 : 1,
  };
}

// ─── Status-to-variant helpers (for status buttons in panels) ─

export const FLOW_STAGE_TONE = {
  ideas: TONE.cyan,
  in_review: TONE.violet,
  todo: TONE.green,
};

export const FLOW_STAGE_VARIANT = {
  ideas: 'cyan',
  in_review: 'purple',
  todo: 'success',
};

export const STATUS_BUTTON_VARIANTS = {
  under_review: 'blue',
  shortlisted: 'purple',
  accepted: 'success',
  waitlist: 'warning',
  rejected: 'danger',
  contacted: 'blue',
};

export const RATING_BUTTON_VARIANTS = {
  average: 'ghost',
  good: 'blue',
  very_good: 'primary',
  genius: 'gold',
};

export const TICKET_DECISION_VARIANTS = {
  accepted: 'success',
  declined: 'danger',
  waitlist: 'warning',
  pending: 'secondary',
};

// ─── Pipeline / chart colors (used on dashboard) ──────────────

export const PIPELINE_COLORS = {
  pending: '#6a6a6a',
  accepted: TONE.green.text,
  declined: TONE.red.text,
  waitlist: TONE.amber.text,
  new: '#5a5a5a',
  under_review: TONE.blue.text,
  shortlisted: TONE.violet.text,
  rejected: TONE.red.text,
  contacted: TONE.paleBlue.text,
  sent: TONE.cyan.text,
  queued: '#5a5a5a',
  failed: TONE.red.text,
};

export { TONE };
